import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  UI_LANGUAGES,
  isUiLanguage,
  languageDirection,
  type UiLanguageCode,
} from '@manpower/shared';
import en from './locales/en.json';

export const LANGUAGE_STORAGE_KEY = 'nxthire.language';

/**
 * Non-English locales are loaded on demand, never bundled.
 *
 * Eleven locales in the initial chunk would make every visitor download ten
 * translations they cannot read, on a platform whose users are mostly on
 * metered mobile data. English ships inline because it is also the fallback:
 * it has to be present before any other locale resolves.
 */
const loaders: Record<Exclude<UiLanguageCode, 'en'>, () => Promise<{ default: object }>> = {
  hi: () => import('./locales/hi.json'),
  bn: () => import('./locales/bn.json'),
  mr: () => import('./locales/mr.json'),
  te: () => import('./locales/te.json'),
  ta: () => import('./locales/ta.json'),
  gu: () => import('./locales/gu.json'),
  ur: () => import('./locales/ur.json'),
  kn: () => import('./locales/kn.json'),
  or: () => import('./locales/or.json'),
  ml: () => import('./locales/ml.json'),
};

const loaded = new Set<string>(['en']);

/** Applies direction and lang to <html>. Urdu is RTL; everything else is not. */
export const applyDocumentLanguage = (code: string): void => {
  const dir = languageDirection(code);
  document.documentElement.setAttribute('lang', code);
  document.documentElement.setAttribute('dir', dir);
};

export const loadLanguage = async (code: string): Promise<void> => {
  if (!isUiLanguage(code) || loaded.has(code) || code === 'en') return;
  try {
    const bundle = await loaders[code]();
    i18n.addResourceBundle(code, 'translation', bundle.default, true, true);
    loaded.add(code);
  } catch {
    // A locale chunk that fails to download must not blank the interface. The
    // English fallback already covers every key, so the user sees a working
    // page in the wrong language rather than a broken one in none.
  }
};

export const changeLanguage = async (code: string): Promise<void> => {
  const target = isUiLanguage(code) ? code : DEFAULT_LANGUAGE;
  await loadLanguage(target);
  await i18n.changeLanguage(target);
  applyDocumentLanguage(target);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, target);
  } catch {
    // Private browsing or blocked storage. The choice simply will not persist.
  }
};

/**
 * Preferred language, best source first: an explicit choice, then the browser,
 * then English. The signed-in user's stored preference is applied separately
 * once the session resolves - it should win over the browser but must not delay
 * first paint waiting for an API call.
 */
const detectInitialLanguage = (): UiLanguageCode => {
  // ?lang= wins over everything, because it is what makes a language its own
  // URL: a link shared in Tamil opens in Tamil on a phone that has never seen
  // this site, and hreflang has something real to point at.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl && isUiLanguage(fromUrl)) return fromUrl;
  } catch {
    /* no window */
  }

  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && isUiLanguage(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  const browser = navigator.language?.split('-')[0];
  if (browser && isUiLanguage(browser)) return browser;
  return DEFAULT_LANGUAGE;
};

const initial = detectInitialLanguage();

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  // Per KEY, not per language: a partially translated locale falls back key by
  // key, so a missing string renders English instead of a raw "jobs.apply" or
  // an empty element. With eleven locales this is the normal case.
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

applyDocumentLanguage(initial);
if (initial !== 'en') void changeLanguage(initial);

/** Languages that have not been checked by a native speaker. */
export const isReviewed = (code: string): boolean =>
  UI_LANGUAGES.find((l) => l.code === code)?.reviewed ?? false;

export default i18n;
